import { db, generateId } from "./db.js";
import { ref } from "vue";
import { getUser } from "./user.js";
import { sendInvites } from "./backend-api.js";
import { getSubscription } from "./subscription.js";
import firebase from "firebase/app";

// ==================== CREATE ====================

async function createFirm(data, userId, transaction) {
  data.owner = userId;
  data.createdAt = firebase.firestore.FieldValue.serverTimestamp();
  // create a new doc ref so we have the id whether or not a transaction is used
  const firmRef = db.collection("firms").doc();
  if (transaction) {
    transaction.set(firmRef, data);
    return { id: firmRef.id, ...data };
  } else {
    await firmRef.set(data);
  }
  const firm = { id: firmRef.id, ...data };
  return firm;
}

// add user as admin member to firm and update user's firms subcollection and firm's members subcollection
// because of the firebase rules, this cannot be done together with createFirm in a transaction
async function addAdminUserToFirm(firmId, userId) {
  const membersRef = db.doc(`firms/${firmId}/members/${userId}`);
  const userFirmRef = db.doc(`users/${userId}/firms/${firmId}`);

  await db.runTransaction(async (transaction) => {
    const firmRef = db.doc(`firms/${firmId}`);
    const firmSnap = await transaction.get(firmRef);
    if (!firmSnap.exists) {
      throw new Error("Firm does not exist");
    }

    const user = await getUser(userId);
    const firmData = firmSnap.data();

    transaction.set(membersRef, {
      uid: userId,
      role: "admin",
      status: "joined",
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      phoneNumber: user.phoneNumber,
    });

    transaction.set(userFirmRef, {
      firmId,
      name: firmData.name,
      role: "admin",
    });
  });
}

// ==================== READ ====================

async function getFirmsByUser(userId) {
  try {
    const snapshot = await db.collection(`users/${userId}/firms`).get();
    return snapshot.docs.map((doc) => ({ ...doc.data() }));
  } catch (error) {
    console.log("Error fetching firms: " + error);
    return [];
  }
}

async function getOrphanedFirmByUser(userId) {
  try {
    const snapshot = await db.collection("firms").where("owner", "==", userId).get();
    if (snapshot.empty) return null;
    const doc = snapshot.docs[0];
    return { id: doc.id, ...doc.data() };
  } catch (error) {
    console.log("Error fetching firms: " + error);
    return null;
  }
}

async function getFirm(firmId) {
  return db.doc(`firms/${firmId}`)
    .get()
    .then(
      (firm) => {
        return firm.exists ? { id: firm.id, ...firm.data() } : null;
      });
}

async function getFirmSettings(firmId) {
  const firm = await getFirm(firmId);
  if (firm) {
    return firm.settings ?? {};
  }
  else {
    return null;
  }
}

async function getFirmLLMs(firmId) {
  const firm_settings = await getFirmSettings(firmId);
  if (firm_settings && firm_settings.llms && firm_settings.llms.length > 0) {
    return firm_settings.llms;
  }
  else {
    return null;
  }
}

async function getExtraApplications(firmId) {
  const firm_settings = await getFirmSettings(firmId);
  if (firm_settings && firm_settings.extra_applications) {
    return firm_settings.extra_applications;
  }
  else {
    return "";
  }
}

async function getDocketReminders(firmId) {
  const firm_settings = await getFirmSettings(firmId);
  if (firm_settings && firm_settings.docket_reminders) {
    return firm_settings.docket_reminders;
  }
  else {
    return [];
  }
}

async function getInitialImportationSetback(firmId) {
  const firm_settings = await getFirmSettings(firmId);
  if (firm_settings && firm_settings.initialImportationSetback) {
    return firm_settings.initialImportationSetback;
  }
  else {
    return 9; // default setback
  }
}

async function getDefaultFirm(userId) {
  const user = await db.doc(`users/${userId}`).get();
  return user.exists ? user.data().defaultFirm : null;
}

async function isUserInFirm(userId, firmId) {
  const membership = await db.doc(`firms/${firmId}/members/${userId}`).get().catch(() => {
    console.log("could not read firm");
    return false;
  });
  // data() is undefined when the membership doc does not exist
  return !!(membership && membership.exists && membership.data()["status"] == "joined");
}

// ==================== UPDATE ====================

function updateFirm(firmId, data) {
  return db.doc(`firms/${firmId}`).update(data);
}

function inviteMember(firmId, role, email = "") {
  const id = generateId();
  return db.doc(`firms/${firmId}/invites/${id}`).set({
    id,
    role,
    status: "invited",
    email,
  }).then(() => sendInvites(firmId));
}

// client portal invite: same invites collection, but role "client" + clientId so
// the backend accept flow links the new member to the CRM client record (portal
// access to shared cases/documents is scoped by that clientId). NOTE: clientId is
// the CORPORATION (CRM client); the invitee is an individual contact there (e.g.
// in-house counsel) — firstName/lastName are the contact's. Language is NOT set —
// the portal UI switches languages live.
// Dedup: one active invite/membership per email per firm — inviting the same
// contact twice must not create a second seat/invite.
async function inviteClient(firmId, clientId, email = "", contact = {}) {
  const existing = await db
    .collection(`firms/${firmId}/invites`)
    .where("email", "==", email)
    .where("status", "in", ["invited", "sent"])
    .get();
  if (!existing.empty) {
    const err = new Error("An invitation for this email is already pending.");
    err.code = "duplicate-invite";
    throw err;
  }
  const members = await db
    .collection(`firms/${firmId}/members`)
    .where("email", "==", email)
    .where("status", "==", "joined")
    .get();
  if (!members.empty) {
    const err = new Error("This email already has portal access.");
    err.code = "duplicate-member";
    throw err;
  }

  const id = generateId();
  return db.doc(`firms/${firmId}/invites/${id}`).set({
    id,
    role: "client",
    clientId,
    status: "invited",
    email,
    ...(contact.firstName ? { firstName: contact.firstName } : {}),
    ...(contact.lastName ? { lastName: contact.lastName } : {}),
  }).then(() => sendInvites(firmId));
}

function getInvites(firmId) {
  const invites = ref([]);
  db.collection(`firms/${firmId}/invites`).get().then((snapshot) => {
    invites.value = snapshot.docs.map((doc) => ({ ...doc.data() }));
  });
  return invites;
}

// ==================== NO DELETE ALLOWED ====================

// firm docketing rules related methods

async function updateDocketingRule(firmId, docketingRule) {
  const settings = await getFirmSettings(firmId);
  if (settings == null) {
    // this means the firm does not exist
    return null;
  }
  else {
    settings.docketingRules = { ...settings.docketingRules, ...docketingRule }
    return updateFirm(firmId, {settings: settings});
  }
}

async function removeDocketingRule(firmId, code) {
  const settings = await getFirmSettings(firmId);
  if (settings == null) {
    // this means the firm does not exist
    return null;
  }
  else {
    if (settings.docketingRules) {
      delete settings.docketingRules[code];
      return updateFirm(firmId, {settings: settings});
    }
    return null;
  }
}

async function getDocketingRule(firmId, code) {
  const settings = await getFirmSettings(firmId);
  if (settings == null) {
    // this means the firm does not exist
    return null;
  }
  else {
    if (settings.docketingRules) {
      return settings.docketingRules[code];
    }
    return null;
  }
}

async function getDocketingRules(firmId) {
  const settings = await getFirmSettings(firmId);
  if (settings == null) {
    return null;
  }
  else {
    return settings.docketingRules ?? {};
  }
}

async function getIgnoreList(firmId) {
  const settings = await getFirmSettings(firmId);
  if (settings == null) {
    return null;
  }
  else {
    return settings.ignoreList ?? [];
  }
}

async function addCodeToIgnoreList(firmId, documentCode) {
  const settings = await getFirmSettings(firmId);
  if (settings == null) {
    // the firm does not exist
    return null;
  }
  else {
    if (settings.ignoreList) {
      if (!settings.ignoreList.includes(documentCode)) {
        settings.ignoreList.push(documentCode);
      }
    }
    else {
      settings.ignoreList = [documentCode];
    }
    return updateFirm(firmId, {settings: settings});
  }
}

async function removeCodeFromIgnoreList(firmId, documentCode) {
  const settings = await getFirmSettings(firmId);
  if (settings == null) {
    // the firm does not exist
    return null;
  }
  else {
    if (settings.ignoreList) {
      const index = settings.ignoreList.indexOf(documentCode);
      if (index !== -1) {
        settings.ignoreList.splice(index, 1);
      }
    }
    return updateFirm(firmId, {settings: settings});
  }
}

async function updateIgnoreList(firmId, newlist) {
  const settings = await getFirmSettings(firmId);
  if (settings == null) {
    // the firm does not exist
    return null;
  }
  else {
    settings.ignoreList = newlist;
    return updateFirm(firmId, {settings: settings});
  }
}

async function getSubscriptionData(firmId) {
  const firm = await getFirm(firmId);
  if (firm) {
    return await getSubscription(firm.subscriptionId);
  }
  return null;
}

async function updateDocketReminders(firmId, docketReminders) {
  const settings = await getFirmSettings(firmId);
  if (settings == null) {
    return null;
  }
  else {
    settings.docket_reminders = docketReminders;
    return updateFirm(firmId, {settings: settings});
  }
}

async function updateExtraApplications(firmId, extraApplications) {
  const settings = await getFirmSettings(firmId);
  if (settings == null) {
    return null;
  }
  else {
    settings.extra_applications = extraApplications;
    return updateFirm(firmId, {settings: settings});
  }
}

// serial numbers imported via the trademark "Import by Serial No." dialog are
// remembered in settings.extra_tm_serials so pairbot's daily firm update
// re-imports them from TSDR — the trademark counterpart of extra_applications
async function getExtraTmSerials(firmId) {
  const firm_settings = await getFirmSettings(firmId);
  if (firm_settings && firm_settings.extra_tm_serials) {
    return firm_settings.extra_tm_serials;
  }
  else {
    return "";
  }
}

async function updateExtraTmSerials(firmId, extraTmSerials) {
  const settings = await getFirmSettings(firmId);
  if (settings == null) {
    return null;
  }
  else {
    settings.extra_tm_serials = extraTmSerials;
    return updateFirm(firmId, {settings: settings});
  }
}

async function UpdateInitialImportationSetback(firmId, setback) {
  // make sure setback is an integer between 3 and 9, inclusive
  if (!Number.isInteger(setback) || setback < 3 || setback > 9) {
    throw new Error("Setback must be an integer between 3 and 9");
  }
  else {
    const settings = await getFirmSettings(firmId);
    if (settings == null) {
      return null;
    }
    else {
      settings.initialImportationSetback = setback;
      return updateFirm(firmId, {settings: settings});
    }
  }
}

// firm-configurable option (firms/{firmId}.settings.maintenanceTaskOption) for
// how far ahead maintenance fee tasks are docketed:
//   'default'  - only phases whose paying window has already opened
//   'all'      - all upcoming unpaid phases, even if the window is not open yet
//   'upcoming' - only the single next unpaid phase, whether its window is open or not
const MAINTENANCE_TASK_OPTION_DEFAULT = 'default';
const MAINTENANCE_TASK_OPTION_ALL = 'all';
const MAINTENANCE_TASK_OPTION_UPCOMING = 'upcoming';
// ordered narrow -> broad: index doubles as rank for the "broadening creates
// additional tasks for already-imported patents" warnings in both settings UIs
const MAINTENANCE_TASK_OPTIONS = [
  MAINTENANCE_TASK_OPTION_DEFAULT,
  MAINTENANCE_TASK_OPTION_UPCOMING,
  MAINTENANCE_TASK_OPTION_ALL,
];

async function getMaintenanceTaskOption(firmId) {
  const settings = await getFirmSettings(firmId);
  if (settings == null) {
    return null;
  }
  return settings.maintenanceTaskOption ?? MAINTENANCE_TASK_OPTION_DEFAULT;
}

async function updateMaintenanceTaskOption(firmId, option) {
  if (!MAINTENANCE_TASK_OPTIONS.includes(option)) {
    throw new Error(`maintenanceTaskOption must be one of: ${MAINTENANCE_TASK_OPTIONS.join(", ")}`);
  }
  const settings = await getFirmSettings(firmId);
  if (settings == null) {
    return null;
  }
  settings.maintenanceTaskOption = option;
  return updateFirm(firmId, {settings: settings});
}

// firm-customized initial-import rules (firms/{firmId}.settings.importRules):
// each rule is { label?, description, deadline: { deadline, unit }, base? } and
// generates one docket task when a patent is first imported. base defaults to
// 'importDate' (deadline counted from the import day); with 'filingDate' the
// deadline counts from the application's filing date and the backend skips the
// rule when the computed deadline has already passed. The validation here
// mirrors the backend's in functions/src/docketing/processPatent.ts.
const IMPORT_RULE_UNITS = ["days", "months", "years"];
const IMPORT_RULE_BASES = ["importDate", "filingDate"];

function validateImportRules(rules) {
  if (!Array.isArray(rules)) {
    throw new Error("importRules must be an array");
  }
  for (const rule of rules) {
    if (!rule || typeof rule.description !== "string" || !rule.description.trim()) {
      throw new Error("Every import rule needs a non-empty description");
    }
    const amount = Number(rule.deadline && rule.deadline.deadline);
    if (!Number.isFinite(amount) || amount <= 0) {
      throw new Error("Every import rule needs a positive deadline number");
    }
    if (!IMPORT_RULE_UNITS.includes(rule.deadline && rule.deadline.unit)) {
      throw new Error(`Import rule unit must be one of: ${IMPORT_RULE_UNITS.join(", ")}`);
    }
    const base = rule.base || "importDate";
    if (!IMPORT_RULE_BASES.includes(base)) {
      throw new Error(`Import rule base must be one of: ${IMPORT_RULE_BASES.join(", ")}`);
    }
  }
}

async function getImportRules(firmId) {
  const settings = await getFirmSettings(firmId);
  if (settings == null) {
    return null;
  }
  return Array.isArray(settings.importRules) ? settings.importRules : [];
}

function normalizeImportRules(rules) {
  return rules.map((rule) => ({
    // stable id: join key for cascade rules (settings.cascadeRules); generated
    // here so rules saved before ids existed acquire one on the next save
    id: typeof rule.id === "string" && rule.id ? rule.id : generateRuleId(),
    label: typeof rule.label === "string" ? rule.label.trim() : "",
    description: rule.description.trim(),
    deadline: { deadline: Number(rule.deadline.deadline), unit: rule.deadline.unit },
    base: rule.base || "importDate",
  }));
}

async function updateImportRules(firmId, rules) {
  validateImportRules(rules);
  const settings = await getFirmSettings(firmId);
  if (settings == null) {
    return null;
  }
  // normalize before persisting: numeric deadline, trimmed strings, explicit base
  settings.importRules = normalizeImportRules(rules);
  return updateFirm(firmId, {settings: settings});
}

// firm-customized cascade rules (firms/{firmId}.settings.cascadeRules):
// each rule is { id, enabled?, trigger: { type: "taskCompleted",
// match: { ruleId } }, deadline: { deadline, unit, base },
// task: { label, description } } and generates one follow-up docket task when a
// task carrying the matched ruleId is marked Completed. The schema and the
// validation here mirror the backend's in functions/src/docketing/docketing-cascade.ts.
const CASCADE_RULE_UNITS = ["days", "months", "years"];
const CASCADE_RULE_BASES = ["eventDate", "triggerTaskDeadline", "filingDate"];

// stable id generator for user-defined rules (import rules, OA custom rules,
// cascade rules) — the join key between a generated task's ruleId and cascade
// trigger.match.ruleId
function generateRuleId() {
  return "rule-" + generateId();
}

function validateCascadeRules(rules) {
  if (!Array.isArray(rules)) {
    throw new Error("cascadeRules must be an array");
  }
  for (const rule of rules) {
    if (!rule || typeof rule.id !== "string" || !rule.id) {
      throw new Error("Every cascade rule needs an id");
    }
    if (!rule.trigger || rule.trigger.type !== "taskCompleted"
        || !rule.trigger.match || typeof rule.trigger.match.ruleId !== "string"
        || !rule.trigger.match.ruleId) {
      throw new Error("Every cascade rule needs a trigger task (the task whose completion starts it)");
    }
    if (!rule.task || typeof rule.task.description !== "string" || !rule.task.description.trim()) {
      throw new Error("Every cascade rule needs a non-empty description");
    }
    const amount = Number(rule.deadline && rule.deadline.deadline);
    if (!Number.isFinite(amount) || amount <= 0) {
      throw new Error("Every cascade rule needs a positive deadline number");
    }
    if (!CASCADE_RULE_UNITS.includes(rule.deadline && rule.deadline.unit)) {
      throw new Error(`Cascade rule unit must be one of: ${CASCADE_RULE_UNITS.join(", ")}`);
    }
    if (!CASCADE_RULE_BASES.includes(rule.deadline && rule.deadline.base)) {
      throw new Error(`Cascade rule base must be one of: ${CASCADE_RULE_BASES.join(", ")}`);
    }
  }
  // cycle detection: an edge match.ruleId -> rule.id means "completing the
  // trigger rule's task spawns this rule's task". Import/OA custom rules are
  // sources only (never targets — nothing spawns tasks with their id except
  // themselves), so cycles can only exist among cascade rules. Chains like
  // A -> B -> C are fine; A -> B -> C -> A is not.
  const edges = new Map(); // source ruleId -> [spawned rule ids]
  for (const rule of rules) {
    const from = rule.trigger.match.ruleId;
    if (!edges.has(from)) edges.set(from, []);
    edges.get(from).push(rule.id);
  }
  const visiting = new Set();
  const done = new Set();
  const visit = (id, path) => {
    if (done.has(id)) return;
    if (visiting.has(id)) {
      throw new Error(`Cascade rules form a circular chain: ${[...path, id].join(" -> ")}`);
    }
    visiting.add(id);
    for (const next of edges.get(id) ?? []) visit(next, [...path, id]);
    visiting.delete(id);
    done.add(id);
  };
  for (const rule of rules) visit(rule.id, []);
}

function normalizeCascadeRules(rules) {
  return rules.map((rule) => ({
    id: rule.id,
    enabled: rule.enabled !== false,
    trigger: { type: "taskCompleted", match: { ruleId: rule.trigger.match.ruleId } },
    deadline: {
      deadline: Number(rule.deadline.deadline),
      unit: rule.deadline.unit,
      base: rule.deadline.base,
    },
    task: {
      label: typeof rule.task.label === "string" ? rule.task.label.trim() : "",
      description: rule.task.description.trim(),
    },
  }));
}

async function getCascadeRules(firmId) {
  const settings = await getFirmSettings(firmId);
  if (settings == null) {
    return null;
  }
  return Array.isArray(settings.cascadeRules) ? settings.cascadeRules : [];
}

async function updateCascadeRules(firmId, rules) {
  validateCascadeRules(rules);
  const settings = await getFirmSettings(firmId);
  if (settings == null) {
    return null;
  }
  settings.cascadeRules = normalizeCascadeRules(rules);
  return updateFirm(firmId, {settings: settings});
}

// customer-number onboarding (add flow): writes the new customer number list
// plus every import setting chosen in the wizard as ONE single-document
// Firestore update, which is atomic — the numbers never go live without the
// settings that govern their initial import
async function saveCustomerNumberSetup(firmId, { customerNumbers, setback, maintenanceTaskOption, importRules }) {
  if (!Array.isArray(customerNumbers) || customerNumbers.some((n) => typeof n !== "string" || !n)) {
    throw new Error("customerNumbers must be an array of non-empty strings");
  }
  if (!Number.isInteger(setback) || setback < 3 || setback > 9) {
    throw new Error("Setback must be an integer between 3 and 9");
  }
  if (!MAINTENANCE_TASK_OPTIONS.includes(maintenanceTaskOption)) {
    throw new Error(`maintenanceTaskOption must be one of: ${MAINTENANCE_TASK_OPTIONS.join(", ")}`);
  }
  const rules = importRules ?? [];
  validateImportRules(rules);
  return updateFirm(firmId, {
    customer_numbers: customerNumbers,
    "settings.initialImportationSetback": setback,
    "settings.maintenanceTaskOption": maintenanceTaskOption,
    "settings.importRules": normalizeImportRules(rules),
  });
}

export {
  createFirm,
  getFirmsByUser,
  inviteMember,
  inviteClient,
  getInvites,
  getDefaultFirm,
  getFirm,
  isUserInFirm,
  updateFirm,
  updateDocketingRule,
  getFirmSettings,
  getDocketingRules,
  getIgnoreList,
  addCodeToIgnoreList,
  removeCodeFromIgnoreList,
  removeDocketingRule,
  updateIgnoreList,
  getDocketingRule,
  getSubscriptionData,
  getFirmLLMs,
  addAdminUserToFirm,
  getOrphanedFirmByUser,
  getDocketReminders,
  updateDocketReminders,
  getExtraApplications,
  getExtraTmSerials,
  updateExtraApplications,
  updateExtraTmSerials,
  getInitialImportationSetback,
  UpdateInitialImportationSetback,
  getMaintenanceTaskOption,
  updateMaintenanceTaskOption,
  MAINTENANCE_TASK_OPTIONS,
  getImportRules,
  updateImportRules,
  saveCustomerNumberSetup,
  validateImportRules,
  IMPORT_RULE_UNITS,
  IMPORT_RULE_BASES,
  getCascadeRules,
  updateCascadeRules,
  validateCascadeRules,
  generateRuleId,
  CASCADE_RULE_UNITS,
  CASCADE_RULE_BASES,
};
